import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, Settings, Eye, Layers, DraftingCompass, FileText, User, MapPin, Check, X, Lock, ArrowLeft, ArrowRight, Menu, Palette, CheckCircle, Mail, ChevronLeft, ChevronRight, Clock, Boxes, SlidersHorizontal, Wrench, Ruler, RulerDimensionLine, Rotate3d, ChevronDown, AlertTriangle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import MainNavigation from '../components/MainNavigation';
import Footer from '../components/Footer';
import ThreeDPreview from '../components/ThreeDPreview';
import { BACKEND_URL } from '../config';
import { trackQuoteSubmission, trackServiceRequest } from '../utils/analytics';

const API = `${BACKEND_URL}/api`;

// Service definitions for the accordion selection
const SERVICES = [
  {
    id: 'on-demand-3d-print',
    name: 'Instant Quote',
    description: 'Upload & get pricing instantly',
    fullDescription: 'Upload your 3D model and receive an instant, accurate price estimate. Our advanced analysis calculates material usage, print time, and complexity in seconds.',
    icon: Upload,
    badge: 'Most Popular',
    badgeColor: 'green',
    features: ['Real-time pricing', '15+ material options', '3D model preview', 'Instant analysis']
  },
  {
    id: 'design',
    name: 'Custom Design',
    description: 'Professional 3D modeling service',
    fullDescription: 'Bring your ideas to life with our expert 3D modeling service. From concept sketches to production-ready models, we handle it all.',
    icon: DraftingCompass,
    badge: 'Full Service',
    badgeColor: 'purple',
    features: ['Expert designers', 'Unlimited revisions', 'CAD formats', 'Fast turnaround']
  },
  {
    id: 'convert',
    name: '2D to 3D',
    description: 'Convert drawings to models',
    fullDescription: 'Transform your 2D drawings, sketches, or blueprints into fully realized 3D models ready for printing or visualization.',
    icon: Layers,
    badge: 'Conversion',
    badgeColor: 'blue',
    features: ['Any 2D format', 'Accurate dimensions', 'Multiple revisions', 'Quick delivery']
  },
  {
    id: 'optimize',
    name: 'Optimize',
    description: 'Improve your 3D models',
    fullDescription: 'Optimize your existing 3D models for better printability, reduced material usage, and improved structural integrity.',
    icon: Settings,
    badge: 'Enhancement',
    badgeColor: 'amber',
    features: ['Printability check', 'Material reduction', 'Strength analysis', 'File repair']
  },
  {
    id: 'renders',
    name: 'Renders',
    description: 'Photorealistic product images',
    fullDescription: 'Get stunning photorealistic renders of your 3D models for marketing, presentations, and e-commerce.',
    icon: Eye,
    badge: 'Visualization',
    badgeColor: 'pink',
    features: ['Photorealistic quality', 'Multiple angles', 'Custom lighting', 'Fast delivery']
  }
];

// Service Selection Accordion Component
const ServiceSelectionAccordion = ({ selectedService, onServiceSelect, onLaunch }) => {
  const [expandedService, setExpandedService] = useState(selectedService || 'on-demand-3d-print');

  const handleServiceClick = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const getBadgeColor = (color) => {
    const colors = {
      green: 'bg-emerald-50 text-emerald-600',
      purple: 'bg-purple-50 text-purple-600',
      blue: 'bg-blue-50 text-blue-600',
      amber: 'bg-amber-50 text-amber-600',
      pink: 'bg-pink-50 text-pink-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col gap-3">
        {SERVICES.map((service) => {
          const isExpanded = expandedService === service.id;
          const Icon = service.icon;
          
          return (
            <div 
              key={service.id}
              className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 ${
                isExpanded 
                  ? 'shadow-xl shadow-blue-500/10 ring-1 ring-blue-100' 
                  : 'shadow-md hover:shadow-lg'
              }`}
            >
              {/* Accordion Header */}
              <button
                type="button"
                onClick={() => handleServiceClick(service.id)}
                className={`w-full flex items-center gap-4 p-5 transition-all duration-300 ${
                  isExpanded 
                    ? 'bg-gradient-to-r from-[#0007b5] to-[#0015e6] text-white' 
                    : 'bg-white hover:bg-slate-50 text-slate-900'
                }`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${
                  isExpanded 
                    ? 'bg-white/20' 
                    : 'bg-slate-100'
                }`}>
                  <Icon className={`w-6 h-6 ${isExpanded ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <div className="flex-1 text-left">
                  <h3 className={`text-lg font-semibold ${isExpanded ? 'text-white' : 'text-slate-900'}`}>
                    {service.name}
                  </h3>
                  <p className={`text-sm ${isExpanded ? 'text-blue-100' : 'text-slate-500'}`}>
                    {service.description}
                  </p>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${
                  isExpanded ? 'rotate-180 text-white' : 'text-slate-400'
                }`} />
              </button>

              {/* Accordion Content */}
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="p-6 pt-2">
                  {/* Description */}
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.fullDescription}
                  </p>

                  {/* Features Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                        <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        </div>
                        <span className="text-sm text-slate-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Preview Area */}
                  <div className="bg-gradient-to-br from-slate-100 to-slate-50 rounded-xl h-40 flex items-center justify-center mb-6 border border-slate-200">
                    <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center">
                      <Icon className="w-9 h-9 text-[#0007b5]" />
                    </div>
                  </div>

                  {/* Launch Button */}
                  <button
                    type="button"
                    onClick={() => onLaunch(service.id)}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#0007b5] text-white rounded-xl font-semibold hover:bg-[#0010d9] transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5"
                  >
                    Launch {service.name}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Warning Modal Component
const SwitchWarningModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Switch Service?</h3>
            <p className="text-sm text-slate-500">Your input may be lost</p>
          </div>
        </div>
        <p className="text-slate-600 mb-6">
          You have unsaved changes in the current service. Switching to another service will reset your progress.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors"
          >
            Stay Here
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-3 bg-[#0007b5] text-white rounded-xl font-medium hover:bg-[#0010d9] transition-colors"
          >
            Switch Anyway
          </button>
        </div>
      </div>
    </div>
  );
};

// Styled Selector Component
const StyledSelector = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const selectedOption = options.find(opt => opt.id === value);

  return (
    <div className="relative mt-2">
      {/* Trigger Button - Same height as Material selector */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-4 bg-white border-2 rounded-xl transition-all duration-300 text-left group min-h-[66px] ${
          isOpen ? 'border-[#0007b5] shadow-lg shadow-blue-500/10' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <span className={selectedOption ? 'text-slate-900 font-semibold' : 'text-slate-400'}>
          {selectedOption ? (
            <span>
              {selectedOption.name}
              {selectedOption.subtitle && <span className="text-slate-400 font-normal ml-2 text-sm">{selectedOption.subtitle}</span>}
            </span>
          ) : placeholder}
        </span>
        {/* Arrow with animation - Same as Material selector */}
        <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#0007b5] rotate-180' : 'group-hover:bg-slate-200'}`}>
          <svg className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'text-white rotate-90' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
      
      {/* Expandable Panel with Fluid Animation - Same as Material selector */}
      <div 
        className={`overflow-hidden transition-all duration-500 ease-out ${
          isOpen ? 'max-h-[400px] opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
        }`}
      >
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-xl overflow-hidden">
          {/* Options List with staggered animation */}
          <div className="p-3 space-y-1 max-h-[300px] overflow-y-auto">
            {options.map((option, index) => {
              const isSelected = value === option.id;
              const isHovered = hoveredId === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    onChange(option.id);
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setHoveredId(option.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 text-left ${
                    isSelected 
                      ? 'bg-[#0007b5] shadow-lg' 
                      : isHovered
                        ? 'bg-slate-50'
                        : 'bg-white hover:bg-slate-50'
                  }`}
                  style={{
                    transitionDelay: `${index * 20}ms`,
                    transform: isOpen ? 'translateX(0)' : 'translateX(-10px)',
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium transition-colors duration-300 ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                      {option.name}
                    </div>
                    {option.subtitle && (
                      <div className={`text-xs transition-colors duration-300 ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                        {option.subtitle}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <Check className="w-5 h-5 text-white flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Material Swatch Selector - Full Version with Images
const MaterialSwatchSelector = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  
  const materials = [
    { 
      id: 'PLA', 
      name: 'PLA', 
      subtitle: 'Standard',
      color: '#10b981',
      gradient: 'from-emerald-400 to-emerald-500',
      bestFor: 'Display models',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/j5yye52r_PLA.jpg'
    },
    { 
      id: 'PLA+', 
      name: 'PLA+', 
      subtitle: 'Reinforced',
      color: '#059669',
      gradient: 'from-emerald-500 to-emerald-600',
      bestFor: 'Light functional parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/j5yye52r_PLA.jpg'
    },
    { 
      id: 'Silk PLA', 
      name: 'Silk PLA', 
      subtitle: 'Glossy finish',
      color: '#ec4899',
      gradient: 'from-pink-400 via-purple-400 to-cyan-400',
      bestFor: 'Visual pieces',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/4vfqnk5j_Silk%20PLA.jpg'
    },
    { 
      id: 'High Temp PLA', 
      name: 'High Temp PLA', 
      subtitle: 'Heat resistant',
      color: '#dc2626',
      gradient: 'from-red-500 to-red-600',
      bestFor: 'Heat-exposed PLA parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/6c7xebfo_High%20temp%20PLA.jpg'
    },
    { 
      id: 'PLA-CF', 
      name: 'PLA-CF', 
      subtitle: 'Carbon Fiber',
      color: '#1e293b',
      gradient: 'from-slate-700 to-slate-900',
      bestFor: 'Structural parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/cjf1rm33_CF%20PLA.jpg'
    },
    { 
      id: 'Metal-filled PLA', 
      name: 'Metal-filled PLA', 
      subtitle: 'Metal finish',
      color: '#71717a',
      gradient: 'from-zinc-400 to-zinc-600',
      bestFor: 'Decorative pieces',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/nx3yf7li_Metallic%20PLA.jpg'
    },
    { 
      id: 'Wood-filled PLA', 
      name: 'Wood-filled PLA', 
      subtitle: 'Wood texture',
      color: '#a16207',
      gradient: 'from-amber-600 to-amber-800',
      bestFor: 'Decorative wood pieces',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/u0syufl0_Wood%20PLA.jpg'
    },
    { 
      id: 'PETG', 
      name: 'PETG', 
      subtitle: 'Durable',
      color: '#0ea5e9',
      gradient: 'from-sky-400 to-sky-500',
      bestFor: 'Functional parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/o7syjr57_PETG.jpg'
    },
    { 
      id: 'PETG-CF', 
      name: 'PETG-CF', 
      subtitle: 'Carbon Fiber',
      color: '#334155',
      gradient: 'from-slate-600 to-slate-800',
      bestFor: 'Functional Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/r6pvkspz_PETG%20CF.jpg'
    },
    { 
      id: 'ABS', 
      name: 'ABS', 
      subtitle: 'Standard',
      color: '#64748b',
      gradient: 'from-slate-400 to-slate-500',
      bestFor: 'General Mechanical Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/3qn26xtd_ABS.jpg'
    },
    { 
      id: 'ABS+', 
      name: 'ABS+', 
      subtitle: 'Reinforced',
      color: '#475569',
      gradient: 'from-slate-500 to-slate-600',
      bestFor: 'General Mechanical Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/3qn26xtd_ABS.jpg'
    },
    { 
      id: 'ASA', 
      name: 'ASA', 
      subtitle: 'UV Resistant',
      color: '#f59e0b',
      gradient: 'from-amber-400 to-amber-500',
      bestFor: 'Mechanical and Functional Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/cslexh2j_ASA.jpg'
    },
    { 
      id: 'TPU 95A', 
      name: 'TPU 95A', 
      subtitle: 'Standard Flexible',
      color: '#f97316',
      gradient: 'from-orange-400 to-orange-500',
      bestFor: 'Bushings, joints',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/jlt30i3g_TPU.jpg'
    },
    { 
      id: 'TPU 64D', 
      name: 'TPU 64D', 
      subtitle: 'Semi-rigid',
      color: '#ea580c',
      gradient: 'from-orange-500 to-orange-600',
      bestFor: 'Gaskets, grips',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/rmp5di0p_TPU%2064D.jpg'
    },
    { 
      id: 'TPU 85A', 
      name: 'TPU 85A', 
      subtitle: 'Soft Flexible',
      color: '#c2410c',
      gradient: 'from-orange-600 to-orange-700',
      bestFor: 'Soft grips, seals',
      thumbnail: 'https://customer-assets.emergentagent.com/job_91777c0b-ca5c-4c46-a071-4765dcb08883/artifacts/fa8oj5bd_TPU%2085A.jpg'
    },
  ];
  
  const selectedMaterial = materials.find(m => m.id === value);
  
  return (
    <div className="relative mt-2">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-white border-2 rounded-xl transition-all duration-300 text-left group ${
          isOpen ? 'border-[#0007b5] shadow-lg shadow-blue-500/10' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        {selectedMaterial ? (
          <div className="flex items-center gap-3">
            {selectedMaterial.thumbnail ? (
              <img 
                src={selectedMaterial.thumbnail} 
                alt={selectedMaterial.name}
                className="w-10 h-10 rounded-xl object-cover shadow-lg transition-transform duration-300 group-hover:scale-110"
              />
            ) : (
              <div 
                className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedMaterial.gradient} shadow-lg transition-transform duration-300 group-hover:scale-110`}
                style={{ boxShadow: `0 4px 14px ${selectedMaterial.color}40` }}
              ></div>
            )}
            <div>
              <span className="font-semibold text-slate-900">{selectedMaterial.name}</span>
              <span className="text-slate-400 ml-2 text-sm">{selectedMaterial.subtitle}</span>
            </div>
          </div>
        ) : (
          <span className="text-slate-400">Choose material</span>
        )}
        <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#0007b5] rotate-180' : 'group-hover:bg-slate-200'}`}>
          <svg className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'text-white rotate-90' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
      
      {/* Expandable Panel with Fluid Animation */}
      <div 
        className={`overflow-hidden transition-all duration-500 ease-out ${
          isOpen ? 'max-h-[700px] opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
        }`}
      >
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-xl overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-slate-900">Select Material</h4>
                <p className="text-xs text-slate-500 mt-0.5">Choose the best material for your project</p>
              </div>
              <a href="/material-guide" className="text-xs text-[#0007b5] hover:underline flex items-center gap-1 font-medium">
                <FileText className="w-3 h-3" />
                Full Guide
              </a>
            </div>
          </div>
          
          {/* Material List - Stylized */}
          <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
            {materials.map((material, index) => {
              const isSelected = value === material.id;
              const isHovered = hoveredId === material.id;
              return (
                <button
                  key={material.id}
                  type="button"
                  onClick={() => {
                    onChange(material.id);
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setHoveredId(material.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-300 text-left group relative ${
                    isSelected 
                      ? 'bg-[#0007b5] shadow-lg' 
                      : isHovered
                        ? 'bg-slate-50'
                        : 'bg-white hover:bg-slate-50'
                  }`}
                  style={{
                    transitionDelay: `${index * 30}ms`,
                    transform: isOpen ? 'translateX(0)' : 'translateX(-20px)',
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  {/* Color Swatch or Thumbnail with Glow Effect */}
                  <div className="relative flex-shrink-0">
                    {material.thumbnail ? (
                      <img 
                        src={material.thumbnail} 
                        alt={material.name}
                        className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-xl object-contain bg-slate-50 shadow-lg transition-all duration-300 ${isHovered || isSelected ? 'scale-105' : ''}`}
                        style={{ 
                          boxShadow: isHovered || isSelected ? `0 8px 24px ${material.color}50` : `0 4px 12px ${material.color}30`
                        }}
                      />
                    ) : (
                      <div 
                        className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-xl bg-gradient-to-br ${material.gradient} shadow-lg transition-all duration-300 ${isHovered || isSelected ? 'scale-105' : ''}`}
                        style={{ 
                          boxShadow: isHovered || isSelected ? `0 8px 24px ${material.color}50` : `0 4px 12px ${material.color}30`
                        }}
                      ></div>
                    )}
                    {isSelected && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center shadow-md">
                        <Check className="w-3 h-3 sm:w-4 sm:h-4 text-[#0007b5]" />
                      </div>
                    )}
                  </div>
                  
                  {/* Material Info */}
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <div className={`font-semibold transition-colors duration-300 ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                      {material.name}
                    </div>
                    <div className={`text-sm transition-colors duration-300 ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                      {material.subtitle}
                    </div>
                    <div className={`mt-1 transition-all duration-300 ${isHovered || isSelected ? 'opacity-100' : 'opacity-70'}`}>
                      <span className={`text-[10px] uppercase tracking-wide ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>Best for</span>
                      <p className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-slate-600'}`}>{material.bestFor}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Styled Dropdown Selector - Same design as Material selector
const StyledDropdownSelector = ({ value, onChange, options, placeholder, title, subtitle, showGuideLink = false, guideLink = "#", guideLinkText = "Learn More" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  
  const selectedOption = options.find(o => o.value === value);
  
  return (
    <div className="relative mt-2">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-white border-2 rounded-xl transition-all duration-300 text-left group ${
          isOpen ? 'border-[#0007b5] shadow-lg shadow-blue-500/10' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        {selectedOption ? (
          <div className="flex items-center gap-3">
            {selectedOption.color && (
              <div 
                className={`w-8 h-8 rounded-lg bg-gradient-to-br ${selectedOption.gradient || 'from-slate-400 to-slate-500'} shadow-md transition-transform duration-300 group-hover:scale-110`}
                style={{ backgroundColor: selectedOption.color }}
              ></div>
            )}
            <div>
              <span className="font-semibold text-slate-900">{selectedOption.label}</span>
              {selectedOption.subtitle && (
                <span className="text-slate-400 ml-2 text-sm">{selectedOption.subtitle}</span>
              )}
            </div>
          </div>
        ) : (
          <span className="text-slate-400">{placeholder || 'Select an option'}</span>
        )}
        <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#0007b5] rotate-180' : 'group-hover:bg-slate-200'}`}>
          <svg className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'text-white rotate-90' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
      
      {/* Expandable Panel with Fluid Animation */}
      <div 
        className={`overflow-hidden transition-all duration-500 ease-out ${
          isOpen ? 'max-h-[400px] opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
        }`}
      >
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-xl overflow-hidden">
          {/* Header */}
          {(title || showGuideLink) && (
            <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div>
                  {title && <h4 className="font-bold text-slate-900">{title}</h4>}
                  {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
                {showGuideLink && (
                  <a href={guideLink} className="text-xs text-[#0007b5] hover:underline flex items-center gap-1 font-medium">
                    <FileText className="w-3 h-3" />
                    {guideLinkText}
                  </a>
                )}
              </div>
            </div>
          )}
          
          {/* Options List */}
          <div className="p-3 space-y-1 max-h-[300px] overflow-y-auto">
            {options.map((option, index) => {
              const isSelected = value === option.value;
              const isHovered = hoveredId === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setHoveredId(option.value)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 text-left ${
                    isSelected 
                      ? 'bg-[#0007b5] shadow-lg' 
                      : isHovered
                        ? 'bg-slate-50'
                        : 'bg-white hover:bg-slate-50'
                  }`}
                  style={{
                    transitionDelay: `${index * 20}ms`,
                    transform: isOpen ? 'translateX(0)' : 'translateX(-10px)',
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  {option.color && (
                    <div 
                      className={`w-6 h-6 rounded-lg shadow-sm transition-all duration-300 ${isHovered || isSelected ? 'scale-110' : ''}`}
                      style={{ 
                        backgroundColor: option.color,
                        boxShadow: isHovered || isSelected ? `0 4px 12px ${option.color}50` : `0 2px 6px ${option.color}30`
                      }}
                    ></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium transition-colors duration-300 ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                      {option.label}
                    </div>
                    {option.subtitle && (
                      <div className={`text-xs transition-colors duration-300 ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                        {option.subtitle}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <Check className="w-5 h-5 text-white flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Service Request Wizard (for 2D to 3D Conversion)
const ServiceRequestWizard = ({ title, description, serviceType, fileRequired, onSubmit, onBackToServices }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [legendOpen, setLegendOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    file: null
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, file });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (fileRequired && !formData.file) {
      alert('File upload is required for this service');
      return;
    }
    onSubmit(serviceType, formData, formData.file);
    setFormData({ name: "", email: "", description: "", file: null });
    setCurrentStep(0);
  };

  const goToStep = (step) => {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setLegendOpen(false);
  };

  const nextStep = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, 1));
  };

  const prevStep = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const getServiceIcon = () => {
    switch(serviceType) {
      case "2D to 3D Conversion": return Layers;
      case "Design Service": return DraftingCompass;
      case "Product Optimization": return Settings;
      case "Product Renders": return Eye;
      default: return FileText;
    }
  };
  const ServiceIcon = getServiceIcon();

  const steps = [
    { title: "Project Details", icon: FileText },
    { title: "Contact Info", icon: User }
  ];

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <>
      {/* Breadcrumb Banner */}
      {onBackToServices && (
        <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-6 md:px-8 py-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToServices}
              className="inline-flex items-center gap-2 text-[#0007b5] hover:text-[#0015e6] font-semibold text-sm transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              All Services
            </button>
            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] border-l-slate-300" />
            <span className="text-slate-700 font-semibold text-sm">{title}</span>
          </div>
        </div>
      )}

      {/* Service Header */}
      <div className="px-6 md:px-8 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0007b5] to-blue-600 flex items-center justify-center shadow-lg">
              <ServiceIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">{title}</h2>
              <p className="text-sm text-slate-500">{description}</p>
            </div>
          </div>
          <div className="text-sm text-slate-500 font-medium hidden sm:block">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Legend Sidebar - Width matches Instant Quote (lg:w-72) */}
        <div className={`
          ${legendOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
          lg:w-72 lg:min-h-full overflow-hidden transition-all duration-300 ease-out
          bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600
        `}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
              <button
                type="button"
                onClick={() => setLegendOpen(false)}
                className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>
            <nav className="space-y-2">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep === index;
                const isCompleted = currentStep > index;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => goToStep(index)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                      isActive 
                        ? 'bg-white/20 text-white shadow-lg' 
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-white text-[#0007b5]' : isCompleted ? 'bg-white/30 text-white' : 'bg-white/10 text-white/60'
                    }`}>
                      {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-white/50">Step {index + 1}</p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 p-6 md:p-8">
          <form onSubmit={handleSubmit}>
            {/* Fixed File Upload Section */}
            <div className="mb-8 pb-8 border-b border-slate-200">
              <div className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-300 ${
                formData.file ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-300 hover:border-[#0007b5] bg-slate-50'
              }`}>
                {formData.file ? (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm md:text-base">{formData.file.name}</p>
                        <p className="text-xs text-slate-500">
                          {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => document.getElementById(`service-file-${serviceType.replace(/\s/g, '-')}`).click()}
                      className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => document.getElementById(`service-file-${serviceType.replace(/\s/g, '-')}`).click()}
                      className="inline-flex items-center justify-center px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#0007b5]/90 transition-colors font-semibold shadow-lg shadow-[#0007b5]/20"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {fileRequired ? "Upload File" : "Upload (Optional)"}
                    </button>
                    <p className="text-slate-500 mt-3 text-sm">
                      CAD files, 3D models, images, PDFs
                    </p>
                  </div>
                )}
                <Input
                  id={`service-file-${serviceType.replace(/\s/g, '-')}`}
                  type="file"
                  accept=".pdf,.dwg,.dxf,.stl,.obj,.3mf,.ply,.amf,.step,.stp,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  required={fileRequired}
                  className="hidden"
                />
              </div>
              <div className="flex items-start gap-2 mt-3 text-xs text-slate-500">
                <Lock className="w-4 h-4 text-slate-700 flex-shrink-0 mt-0.5 font-bold" />
                <p><span className="font-bold text-slate-700">You own your ideas and designs.</span> All uploads are confidential and not shared with any third parties.</p>
              </div>
            </div>

            {/* Animated Step Content */}
            <div className="relative overflow-hidden min-h-[280px]">
              {/* Step 0: Project Details */}
              <div 
                className={`transition-all duration-300 ease-out ${
                  currentStep === 0 
                    ? 'opacity-100 translate-x-0' 
                    : direction > 0 
                      ? 'opacity-0 -translate-x-full absolute inset-0' 
                      : 'opacity-0 translate-x-full absolute inset-0'
                }`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Project Details</h3>
                <div>
                  <Label className="text-base font-medium">Describe your project</Label>
                  <textarea
                    placeholder="Describe your project in detail..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows={5}
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none resize-none"
                  />
                </div>
              </div>

              {/* Step 1: Contact Info */}
              <div 
                className={`transition-all duration-300 ease-out ${
                  currentStep === 1 
                    ? 'opacity-100 translate-x-0' 
                    : direction > 0 
                      ? 'opacity-0 translate-x-full absolute inset-0' 
                      : 'opacity-0 -translate-x-full absolute inset-0'
                }`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">Name</Label>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Email</Label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
              <button
                type="button"
                onClick={prevStep}
                className={`flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors ${currentStep === 0 ? 'invisible' : ''}`}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              {currentStep < totalSteps - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#000690] transition-colors font-medium"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <Button 
                  type="submit" 
                  className="px-6 py-3 bg-[#0007b5] hover:bg-[#000690] text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20"
                >
                  Submit Request
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Service Request Wizard with 3D Preview (for Optimize and Renders tabs)
const ServiceRequestWizardWith3DPreview = ({ title, description, serviceType, onSubmit, onBackToServices }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [legendOpen, setLegendOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    file: null
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, file });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.file) {
      alert('File upload is required for this service');
      return;
    }
    onSubmit(serviceType, formData, formData.file);
    setFormData({ name: "", email: "", description: "", file: null });
    setCurrentStep(0);
  };

  const goToStep = (step) => {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setLegendOpen(false);
  };

  const nextStep = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, 1));
  };

  const prevStep = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const getServiceIcon = () => {
    switch(serviceType) {
      case "Product Optimization": return Settings;
      case "Product Renders": return Eye;
      default: return FileText;
    }
  };
  const ServiceIcon = getServiceIcon();

  const steps = [
    { title: "Project Details", icon: FileText },
    { title: "Contact Info", icon: User }
  ];

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <>
      {/* Breadcrumb Banner */}
      {onBackToServices && (
        <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-6 md:px-8 py-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToServices}
              className="inline-flex items-center gap-2 text-[#0007b5] hover:text-[#0015e6] font-semibold text-sm transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              All Services
            </button>
            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] border-l-slate-300" />
            <span className="text-slate-700 font-semibold text-sm">{title}</span>
          </div>
        </div>
      )}

      {/* Service Header */}
      <div className="px-6 md:px-8 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0007b5] to-blue-600 flex items-center justify-center shadow-lg">
              <ServiceIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">{title}</h2>
              <p className="text-sm text-slate-500">{description}</p>
            </div>
          </div>
          <div className="text-sm text-slate-500 font-medium hidden sm:block">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Legend Sidebar - Width matches Instant Quote (lg:w-72) */}
        <div className={`
          ${legendOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
          lg:w-72 lg:min-h-full overflow-hidden transition-all duration-300 ease-out
          bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600
        `}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
              <button
                type="button"
                onClick={() => setLegendOpen(false)}
                className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>
            <nav className="space-y-2">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep === index;
                const isCompleted = currentStep > index;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => goToStep(index)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                      isActive 
                        ? 'bg-white/20 text-white shadow-lg' 
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-white text-[#0007b5]' : isCompleted ? 'bg-white/30 text-white' : 'bg-white/10 text-white/60'
                    }`}>
                      {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-white/50">Step {index + 1}</p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 p-6 md:p-8">
          <form onSubmit={handleSubmit}>
            {/* Fixed 3D Preview Section */}
            <div className="mb-8 pb-8 border-b border-slate-200">
              <div className={`rounded-2xl overflow-hidden transition-all duration-300 ${
                formData.file 
                  ? 'bg-slate-50 border-2 border-dashed border-slate-300' 
                  : 'bg-slate-50 border-2 border-dashed border-slate-300 hover:border-[#0007b5] cursor-pointer'
              }`} onClick={(e) => {
                if (!formData.file && e.target.tagName !== 'INPUT') {
                  document.getElementById(`3d-file-${serviceType.replace(/\s/g, '-')}`).click();
                }
              }}>
                {formData.file ? (
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200 flex-wrap gap-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-[#0007b5] rounded-lg flex items-center justify-center">
                          <FileText className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900 truncate max-w-[150px] md:max-w-xs">
                            {formData.file.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => document.getElementById(`3d-file-${serviceType.replace(/\s/g, '-')}`).click()}
                        className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium transition-colors"
                      >
                        Change File
                      </button>
                    </div>
                    <div className="w-full min-h-[300px] rounded-xl overflow-hidden bg-gradient-to-b from-slate-200 to-slate-300">
                      <ThreeDPreview 
                        file={formData.file}
                        onAnalysis={(analysisData) => {
                          console.log('3D Analysis:', analysisData);
                        }}
                        onFileModified={(modifiedFile) => {
                          console.log('File modified:', modifiedFile.name);
                          setFormData(prev => ({ ...prev, file: modifiedFile }));
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <div className="w-20 h-20 bg-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Rotate3d className="w-10 h-10 text-slate-400" />
                    </div>
                    <p className="text-xl font-semibold text-slate-900 mb-2">Upload your 3D model</p>
                    <p className="text-slate-500 mb-6">You can view and verify your 3D model here</p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById(`3d-file-${serviceType.replace(/\s/g, '-')}`).click();
                      }}
                      className="inline-flex items-center justify-center px-8 py-4 bg-[#0007b5] text-white rounded-xl hover:bg-[#0007b5]/90 transition-colors font-semibold shadow-lg shadow-[#0007b5]/20 text-lg"
                    >
                      <Upload className="w-5 h-5 mr-2" />
                      Browse 3D Files
                    </button>
                    <p className="text-slate-400 mt-4 text-sm">
                      STL, OBJ, 3MF, STEP, PLY files supported
                    </p>
                  </div>
                )}
                <Input
                  id={`3d-file-${serviceType.replace(/\s/g, '-')}`}
                  type="file"
                  accept=".stl,.obj,.3mf,.ply,.step,.stp"
                  onChange={handleFileUpload}
                  onClick={(e) => e.stopPropagation()}
                  required
                  className="hidden"
                />
              </div>
              <div className="flex items-start gap-2 mt-3 text-xs text-slate-500">
                <Lock className="w-4 h-4 text-slate-700 flex-shrink-0 mt-0.5" />
                <p><span className="font-bold text-slate-700">You own your ideas and designs.</span> All uploads are confidential and not shared with any third parties.</p>
              </div>
            </div>

            {/* Animated Step Content */}
            <div className="relative overflow-hidden min-h-[280px]">
              {/* Step 0: Project Details */}
              <div 
                className={`transition-all duration-300 ease-out ${
                  currentStep === 0 
                    ? 'opacity-100 translate-x-0' 
                    : direction > 0 
                      ? 'opacity-0 -translate-x-full absolute inset-0' 
                      : 'opacity-0 translate-x-full absolute inset-0'
                }`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Project Details</h3>
                <div>
                  <Label className="text-base font-medium">Describe your project</Label>
                  <textarea
                    placeholder="Describe your project in detail..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows={5}
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none resize-none"
                  />
                </div>
              </div>

              {/* Step 1: Contact Info */}
              <div 
                className={`transition-all duration-300 ease-out ${
                  currentStep === 1 
                    ? 'opacity-100 translate-x-0' 
                    : direction > 0 
                      ? 'opacity-0 translate-x-full absolute inset-0' 
                      : 'opacity-0 -translate-x-full absolute inset-0'
                }`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">Name</Label>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Email</Label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
              <button
                type="button"
                onClick={prevStep}
                className={`flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors ${currentStep === 0 ? 'invisible' : ''}`}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              {currentStep < totalSteps - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#000690] transition-colors font-medium"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <Button 
                  type="submit" 
                  className="px-6 py-3 bg-[#0007b5] hover:bg-[#000690] text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20"
                >
                  Submit Request
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Design Service Wizard with expanded project details (5-step wizard)
const DesignServiceWizard = ({ onSubmit, onBackToServices }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [legendOpen, setLegendOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    description: "",
    file: null,
    application: "",
    applicationOther: "",
    configuration: "",
    printingRequirements: "",
    fitmentRequirements: "",
    timeline: "",
    timelineOther: "",
    optionalServices: []
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, file });
  };

  const handleOptionalServiceChange = (service) => {
    setFormData(prev => ({
      ...prev,
      optionalServices: prev.optionalServices.includes(service)
        ? prev.optionalServices.filter(s => s !== service)
        : [...prev.optionalServices, service]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      application: formData.application === 'other' ? formData.applicationOther : formData.application,
      timeline: formData.timeline === 'other' ? formData.timelineOther : formData.timeline
    };
    onSubmit("Design Service", submitData, formData.file);
    setFormData({ 
      name: "", email: "", description: "", file: null,
      application: "", applicationOther: "", configuration: "", 
      printingRequirements: "", fitmentRequirements: "", 
      timeline: "", timelineOther: "", optionalServices: []
    });
    setCurrentStep(0);
  };

  const goToStep = (step) => {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setLegendOpen(false);
  };

  const nextStep = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const applicationOptions = [
    { value: "auto-interior", label: "Auto Interior" },
    { value: "auto-suspension", label: "Auto Suspension" },
    { value: "auto-engine-bay", label: "Auto Engine Bay" },
    { value: "drone-chassis", label: "Drone Chassis" },
    { value: "drone-accessories", label: "Drone Accessories" },
    { value: "organization", label: "Organization" },
    { value: "decorative", label: "Decorative" },
    { value: "storage", label: "Storage" },
    { value: "sim-racing", label: "Sim Racing" },
    { value: "flight-sim", label: "Flight Sim" },
    { value: "other", label: "Other" }
  ];

  const configurationOptions = [
    { value: "individual", label: "Individual piece" },
    { value: "assembly", label: "Assembly" },
    { value: "not-sure", label: "Not sure" }
  ];

  const printingRequirementsOptions = [
    { value: "prototyping-only", label: "Prototyping only" },
    { value: "prototyping-and-production", label: "Prototyping and final production" }
  ];

  const fitmentOptions = [
    { value: "no-fitment", label: "No fitment required with other parts" },
    { value: "provide-dimensions", label: "I will provide dimensions" },
    { value: "provide-3d-scan", label: "I will provide 3D scan(s)" },
    { value: "coordinated-by-c3d", label: "I need fitment coordinated by C3D Works" }
  ];

  const timelineOptions = [
    { value: "2-4-days", label: "2-4 days" },
    { value: "1-2-weeks", label: "1-2 weeks" },
    { value: "1-month-plus", label: "1 month or more" },
    { value: "other", label: "Other - please specify" }
  ];

  const optionalServicesOptions = [
    { value: "technical-drawings", label: "Technical drawings" },
    { value: "renders", label: "Renders" },
    { value: "production-parts", label: "Production of final parts" }
  ];

  const steps = [
    { title: "Project Details", icon: FileText },
    { title: "Application", icon: Wrench },
    { title: "Requirements", icon: RulerDimensionLine },
    { title: "Timeline", icon: Clock },
    { title: "Contact", icon: User }
  ];

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <>
      {/* Breadcrumb Banner */}
      {onBackToServices && (
        <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-6 md:px-8 py-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToServices}
              className="inline-flex items-center gap-2 text-[#0007b5] hover:text-[#0015e6] font-semibold text-sm transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              All Services
            </button>
            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] border-l-slate-300" />
            <span className="text-slate-700 font-semibold text-sm">Custom Design</span>
          </div>
        </div>
      )}

      {/* Service Header */}
      <div className="px-6 md:px-8 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0007b5] to-blue-600 flex items-center justify-center shadow-lg">
              <DraftingCompass className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Custom Design Service</h2>
              <p className="text-sm text-slate-500">Need a custom 3D model? Our design team can bring your ideas to life.</p>
            </div>
          </div>
          <div className="text-sm text-slate-500 font-medium hidden sm:block">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

    <div className="flex flex-col lg:flex-row">
      {/* Legend Sidebar - Width matches Instant Quote (lg:w-72) */}
      <div className={`
        ${legendOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
        lg:w-72 lg:min-h-full overflow-hidden transition-all duration-300 ease-out
        bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600
      `} data-testid="design-form-legend-sidebar">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
            <button
              type="button"
              onClick={() => setLegendOpen(false)}
              className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4 text-white/80" />
            </button>
          </div>
          <nav className="space-y-2">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === index;
              const isCompleted = currentStep > index;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => goToStep(index)}
                  data-testid={`design-form-step-${index}`}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                    isActive 
                      ? 'bg-white/20 text-white shadow-lg' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isActive ? 'bg-white text-[#0007b5]' : isCompleted ? 'bg-white/30 text-white' : 'bg-white/10 text-white/60'
                  }`}>
                    {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-white/50">Step {index + 1}</p>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 p-6 md:p-8">
        <form onSubmit={handleSubmit}>
          {/* Fixed File Upload Section */}
          <div className="mb-8 pb-8 border-b border-slate-200">
            <div className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-300 ${
              formData.file ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-300 hover:border-[#0007b5] bg-slate-50'
            }`}>
              {formData.file ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{formData.file.name}</p>
                      <p className="text-xs text-slate-500">
                        {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => document.getElementById('design-service-file').click()}
                    className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => document.getElementById('design-service-file').click()}
                    className="inline-flex items-center justify-center px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#0007b5]/90 transition-colors font-semibold shadow-lg shadow-[#0007b5]/20"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Reference (Optional)
                  </button>
                  <p className="text-slate-500 mt-3 text-sm">
                    Images, PDFs, CAD files, sketches
                  </p>
                </div>
              )}
              <Input
                id="design-service-file"
                type="file"
                accept=".pdf,.dwg,.dxf,.stl,.obj,.3mf,.ply,.amf,.step,.stp,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
            <div className="flex items-start gap-2 mt-3 text-xs text-slate-500">
              <Lock className="w-4 h-4 text-slate-700 flex-shrink-0 mt-0.5" />
              <p><span className="font-bold text-slate-700">You own your ideas and designs.</span> All uploads are confidential and not shared with any third parties.</p>
            </div>
          </div>

          {/* Animated Step Content */}
          <div className="relative overflow-hidden min-h-[280px]">
            {/* Step 0: Project Details */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 0 
                  ? 'opacity-100 translate-x-0' 
                  : direction > 0 
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Project Details</h3>
              <div>
                <Label className="text-base font-medium">Describe your project</Label>
                <textarea
                  placeholder="Tell us about your idea, what you're trying to achieve, and any specific requirements..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  rows={7}
                  className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none resize-none"
                />
              </div>
            </div>

            {/* Step 1: Application & Configuration */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 1 
                  ? 'opacity-100 translate-x-0' 
                  : currentStep > 1
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Application & Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-medium">Application</Label>
                  <StyledSelector
                    value={formData.application}
                    onChange={(v) => setFormData({...formData, application: v})}
                    placeholder="Select application type"
                    options={applicationOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                  {formData.application === 'other' && (
                    <input
                      type="text"
                      placeholder="Please specify..."
                      value={formData.applicationOther}
                      onChange={(e) => setFormData({...formData, applicationOther: e.target.value})}
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-base font-medium">Configuration</Label>
                  <StyledSelector
                    value={formData.configuration}
                    onChange={(v) => setFormData({...formData, configuration: v})}
                    placeholder="Select configuration"
                    options={configurationOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                </div>
              </div>
            </div>

            {/* Step 2: Requirements */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 2 
                  ? 'opacity-100 translate-x-0' 
                  : currentStep > 2
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Requirements</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-medium">3D Printing Requirements</Label>
                  <StyledSelector
                    value={formData.printingRequirements}
                    onChange={(v) => setFormData({...formData, printingRequirements: v})}
                    placeholder="Select printing requirements"
                    options={printingRequirementsOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                </div>
                <div>
                  <Label className="text-base font-medium">Fitment Requirements</Label>
                  <StyledSelector
                    value={formData.fitmentRequirements}
                    onChange={(v) => setFormData({...formData, fitmentRequirements: v})}
                    placeholder="Select fitment requirements"
                    options={fitmentOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                </div>
              </div>
            </div>

            {/* Step 3: Timeline & Services */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 3 
                  ? 'opacity-100 translate-x-0' 
                  : currentStep > 3
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Timeline & Services</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-medium">How soon do you need a concept?</Label>
                  <StyledSelector
                    value={formData.timeline}
                    onChange={(v) => setFormData({...formData, timeline: v})}
                    placeholder="Select timeline"
                    options={timelineOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                  {formData.timeline === 'other' && (
                    <input
                      type="text"
                      placeholder="Please specify timeline..."
                      value={formData.timelineOther}
                      onChange={(e) => setFormData({...formData, timelineOther: e.target.value})}
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-base font-medium">Optional Services</Label>
                  <div className="mt-2 space-y-3">
                    {optionalServicesOptions.map(option => (
                      <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          formData.optionalServices.includes(option.value)
                            ? 'bg-[#0007b5] border-[#0007b5]'
                            : 'border-slate-300 group-hover:border-[#0007b5]'
                        }`}>
                          {formData.optionalServices.includes(option.value) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.optionalServices.includes(option.value)}
                          onChange={() => handleOptionalServiceChange(option.value)}
                          className="hidden"
                        />
                        <span className="text-slate-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4: Contact Info */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 4 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-medium">Name</Label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                  />
                </div>
                <div>
                  <Label className="text-base font-medium">Email</Label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={prevStep}
              className={`flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors ${currentStep === 0 ? 'invisible' : ''}`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {currentStep < totalSteps - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#000690] transition-colors font-medium"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <Button 
                type="submit" 
                className="px-8 py-3 bg-[#0007b5] hover:bg-[#000690] text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20"
              >
                Submit Request
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

const ServicesToolPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('service') || searchParams.get('tab') || null;
  const startInTool = !!initialTab;
  
  const [activeTab, setActiveTab] = useState(initialTab || 'on-demand-3d-print');
  const [showServiceSelection, setShowServiceSelection] = useState(!startInTool);
  const [showSwitchWarning, setShowSwitchWarning] = useState(false);
  const [pendingService, setPendingService] = useState(null);
  const [hasFormData, setHasFormData] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [detectedUnits, setDetectedUnits] = useState({ units: null, message: null });
  
  // Quote Wizard State
  const [quoteStep, setQuoteStep] = useState(0);
  const quoteSteps = [
    { title: "Material & Quality", icon: Boxes, fields: ["material", "quality"] },
    { title: "Options", icon: SlidersHorizontal, fields: ["strength", "smoothing"] },
    { title: "Color & Quantity", icon: Palette, fields: ["color", "quantity"] },
    { title: "Contact Info", icon: User, fields: ["contact_name", "contact_email"] },
    { title: "Shipping", icon: MapPin, fields: ["country_code", "postal_code"] },
    { title: "Review & Submit", icon: FileText, fields: ["notes"] }
  ];
  const totalQuoteSteps = quoteSteps.length;
  const quoteProgress = ((quoteStep + 1) / totalQuoteSteps) * 100;

  const [formData, setFormData] = useState({
    file: null,
    material: "PLA",
    strength: "Reinforced",
    quality: "Prototype",
    color: "Any",
    customColor: "",
    quantity: 1,
    contact_name: "",
    contact_email: "",
    country_code: "CA",
    postal_code: "",
    notes: "",
    units: "mm",
    smoothing: "None - As Printed"
  });

  // Note: Service form state is now managed internally by each wizard component
  // Only keeping submittingService for compatibility with handleServiceRequest
  const [submittingService, setSubmittingService] = useState(false);

  // Handle launching a service from the selection screen
  const handleLaunchService = (serviceId) => {
    setActiveTab(serviceId);
    setShowServiceSelection(false);
    setSearchParams({ service: serviceId });
  };

  // Handle going back to service selection
  const handleBackToServices = () => {
    // Check if there's any form data (simplified check - could be made more robust)
    if (formData.file || formData.contact_name || formData.contact_email) {
      setShowSwitchWarning(true);
      setPendingService(null);
    } else {
      setShowServiceSelection(true);
      setSearchParams({});
    }
  };

  // Handle confirming the switch
  const handleConfirmSwitch = () => {
    setShowSwitchWarning(false);
    if (pendingService) {
      setActiveTab(pendingService);
      setSearchParams({ service: pendingService });
      setPendingService(null);
    } else {
      setShowServiceSelection(true);
      setSearchParams({});
    }
    // Reset form data
    setFormData({
      file: null,
      material: "PLA",
      strength: "Reinforced",
      quality: "Prototype",
      color: "Any",
      customColor: "",
      quantity: 1,
      contact_name: "",
      contact_email: "",
      country_code: "CA",
      postal_code: "",
      notes: "",
      units: "mm",
      smoothing: "None - As Printed"
    });
    setQuoteStep(0);
    setQuoteData(null);
  };

  const nextQuoteStep = () => {
    if (quoteStep < totalQuoteSteps - 1) {
      setQuoteStep(prev => prev + 1);
    }
  };

  const prevQuoteStep = () => {
    if (quoteStep > 0) {
      setQuoteStep(prev => prev - 1);
    }
  };

  const goToQuoteStep = (step) => {
    setQuoteStep(step);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setLoadingQuote(true);
    
    try {
      const formDataToSend = new FormData();
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }
      formDataToSend.append('material', formData.material);
      formDataToSend.append('strength', formData.strength);
      formDataToSend.append('quality', formData.quality);
      formDataToSend.append('color', formData.color === 'Other' ? formData.customColor : formData.color);
      formDataToSend.append('quantity', formData.quantity.toString());
      formDataToSend.append('contact_name', formData.contact_name);
      formDataToSend.append('contact_email', formData.contact_email);
      formDataToSend.append('country_code', formData.country_code);
      formDataToSend.append('postal_code', formData.postal_code);
      formDataToSend.append('notes', formData.notes);
      formDataToSend.append('units', formData.units);
      formDataToSend.append('smoothing', formData.smoothing);

      const response = await fetch(`${API}/quote`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        const data = await response.json();
        setQuoteData(data);
        setActiveTab('quote');
        trackQuoteSubmission(formData.material, formData.quantity);
      }
    } catch (error) {
      console.error('Quote submission error:', error);
    } finally {
      setLoadingQuote(false);
    }
  };

  const handleServiceRequest = async (serviceType, serviceFormData, file = null) => {
    setSubmittingService(true);
    try {
      let endpoint;
      let body;
      let headers = {};

      if (serviceType === "2D to 3D Conversion" || serviceType === "Product Optimization" || serviceType === "Renders") {
        if (!file) {
          alert('File upload is required for this service');
          setSubmittingService(false);
          return;
        }
        endpoint = `${API}/request/service-with-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', serviceFormData.name);
        body.append('contact_email', serviceFormData.email);
        body.append('description', serviceFormData.description);
        body.append('file', file);
      } else if (serviceType === "Design Service") {
        endpoint = `${API}/request/service-optional-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', serviceFormData.name);
        body.append('contact_email', serviceFormData.email);
        body.append('description', serviceFormData.description);
        if (file) {
          body.append('file', file);
        }
      } else {
        endpoint = `${API}/request/service`;
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({
          service_type: serviceType,
          contact_name: serviceFormData.name,
          contact_email: serviceFormData.email,
          description: serviceFormData.description,
          files_attached: false
        });
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: body,
      });

      if (response.ok) {
        trackServiceRequest(serviceType);
        alert('Service request submitted successfully! We will contact you within 24 hours.');
        // Reset the form
        if (serviceType === "Design Service") {
          setDesignForm({ name: '', email: '', description: '', file: null });
        } else if (serviceType === "2D to 3D Conversion") {
          setConvertForm({ name: '', email: '', description: '', file: null });
        } else if (serviceType === "Product Optimization") {
          setOptimizeForm({ name: '', email: '', description: '', file: null });
        } else if (serviceType === "Renders") {
          setRendersForm({ name: '', email: '', description: '', file: null });
        }
      } else {
        alert('Error submitting service request. Please try again.');
      }
    } catch (error) {
      console.error('Service request error:', error);
      alert('Error submitting service request. Please try again.');
    } finally {
      setSubmittingService(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <MainNavigation />
      
      {/* Switch Warning Modal */}
      <SwitchWarningModal 
        isOpen={showSwitchWarning}
        onConfirm={handleConfirmSwitch}
        onCancel={() => setShowSwitchWarning(false)}
      />
      
      {/* Full Width Services Tool */}
      <div className="pt-20">
        {/* Hero Header */}
        <div className="bg-gradient-to-br from-[#00068a] via-[#000cb8] to-[#0015e6] text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Start Your Project
              </h1>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Get an instant print-on-demand quote, or request our services at any stage of your project from concept to finished product
              </p>
            </div>
          </div>
        </div>

        {/* Conditional Content: Service Selection OR Tool */}
        {showServiceSelection ? (
          /* Service Selection Accordion View */
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-16">
            <ServiceSelectionAccordion 
              selectedService={activeTab}
              onServiceSelect={(id) => setActiveTab(id)}
              onLaunch={handleLaunchService}
            />
          </div>
        ) : (
          /* Tool View */
          <>
            {/* Main Tool Container - Overlapping Header */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Unified Form Container */}
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200/60 overflow-hidden">

              {/* Upload & Quote Tab */}
              <TabsContent value="on-demand-3d-print" className="mt-0">
                {/* Breadcrumb Banner */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-6 md:px-8 py-3 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleBackToServices}
                      className="inline-flex items-center gap-2 text-[#0007b5] hover:text-[#0015e6] font-semibold text-sm transition-colors group"
                    >
                      <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                      All Services
                    </button>
                    <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] border-l-slate-300" />
                    <span className="text-slate-700 font-semibold text-sm">Instant Quote</span>
                  </div>
                </div>

                {/* Service Header */}
                <div className="px-6 md:px-8 py-4 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0007b5] to-blue-600 flex items-center justify-center shadow-lg">
                        <Upload className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl md:text-2xl font-bold text-slate-900">Instant Quote</h2>
                        <p className="text-sm text-slate-500">Upload your 3D file and configure your print</p>
                      </div>
                    </div>
                    <div className="text-sm text-slate-500 font-medium hidden sm:block">
                      Step {quoteStep + 1} of {totalQuoteSteps}
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-4 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
                      style={{ width: `${quoteProgress}%` }}
                    />
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row">
                  {/* Sidebar */}
                  <div className="hidden lg:block lg:w-72 bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600">
                    <div className="p-6">
                      <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
                      <nav className="space-y-2 mt-4">
                        {quoteSteps.map((step, index) => {
                          const StepIcon = step.icon;
                          const isActive = quoteStep === index;
                          const isCompleted = quoteStep > index;
                          return (
                            <button
                              key={index}
                              type="button"
                              onClick={() => goToQuoteStep(index)}
                              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                                isActive 
                                  ? 'bg-white/20 text-white shadow-lg' 
                                  : 'text-white/70 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isActive ? 'bg-white text-[#0007b5]' : isCompleted ? 'bg-white/30 text-white' : 'bg-white/10 text-white/60'
                              }`}>
                                {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                                  {step.title}
                                </p>
                                <p className="text-xs text-white/50">Step {index + 1}</p>
                              </div>
                            </button>
                          );
                        })}
                      </nav>
                    </div>
                  </div>

                  {/* Form Content */}
                  <div className="flex-1 p-6 md:p-8">
                    <form onSubmit={handleQuoteSubmit}>
                      {/* File Upload - Always visible */}
                      <div className="mb-8 pb-8 border-b border-slate-200">
                        <div className={`relative rounded-2xl transition-all duration-300 overflow-hidden ${
                          formData.file 
                            ? 'bg-slate-50 border-2 border-dashed border-slate-300' 
                            : 'bg-slate-50 border-2 border-dashed border-slate-300 hover:border-[#0007b5]'
                        }`}>
                          {formData.file ? (
                            <div className="p-6 bg-slate-50">
                              <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-[#0007b5] rounded-lg flex items-center justify-center">
                                    <FileText className="h-5 w-5 text-white" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900 truncate max-w-xs">
                                      {formData.file.name}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => document.getElementById('file-upload').click()}
                                  className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium transition-colors"
                                >
                                  Change File
                                </button>
                              </div>
                              <ThreeDPreview 
                                file={formData.file}
                                material={formData.material}
                                strength={formData.strength}
                                quality={formData.quality}
                                units={formData.units}
                                onAnalysis={(analysisData) => {
                                  if (analysisData.detectedUnits) {
                                    setDetectedUnits({
                                      units: analysisData.detectedUnits.units,
                                      message: analysisData.detectedUnits.message
                                    });
                                    if (analysisData.detectedUnits.units !== 'unknown') {
                                      setFormData(prev => ({...prev, units: analysisData.detectedUnits.units}));
                                    }
                                  }
                                }}
                                onFileModified={(modifiedFile) => {
                                  console.log('File modified:', modifiedFile.name);
                                  setFormData(prev => ({ ...prev, file: modifiedFile }));
                                }}
                              />
                            </div>
                          ) : (
                            <div 
                              className="p-12 text-center cursor-pointer"
                              onClick={() => document.getElementById('file-upload').click()}
                            >
                              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-200 flex items-center justify-center">
                                <Rotate3d className="w-10 h-10 text-slate-400" />
                              </div>
                              <p className="text-xl font-semibold text-slate-900 mb-2">
                                Upload your 3D model
                              </p>
                              <p className="text-slate-500 mb-6">
                                You can view and verify your 3D model here
                              </p>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  document.getElementById('file-upload').click();
                                }}
                                className="inline-flex items-center justify-center px-8 py-4 bg-[#0007b5] text-white rounded-xl hover:bg-[#0007b5]/90 transition-colors font-semibold shadow-lg shadow-[#0007b5]/20 text-lg"
                              >
                                <Upload className="w-5 h-5 mr-2" />
                                Browse 3D Files
                              </button>
                              <p className="text-slate-400 mt-4 text-sm">
                                STL, OBJ, 3MF, STEP, PLY files supported
                              </p>
                            </div>
                          )}
                          <Input
                            id="file-upload"
                            type="file"
                            accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setFormData({...formData, file: file});
                                setDetectedUnits({ units: null, message: null });
                              }
                            }}
                            className="hidden"
                          />
                        </div>
                        <div className="flex items-start gap-2 mt-3 text-xs text-slate-500">
                          <Lock className="w-4 h-4 text-slate-700 flex-shrink-0 mt-0.5" />
                          <p><span className="font-bold text-slate-700">You own your ideas and designs.</span> All uploads are confidential and not shared with any third parties.</p>
                        </div>
                      </div>

                      {/* Wizard Steps */}
                      <div className="relative overflow-hidden min-h-[320px]">
                        {/* Step 0: Material & Quality */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 0 ? 'opacity-100 translate-x-0' : quoteStep > 0 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Material & Quality</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Material</Label>
                              <MaterialSwatchSelector value={formData.material} onChange={(v) => setFormData({...formData, material: v})} />
                            </div>
                            <div>
                              <Label className="text-base font-medium">Print Quality</Label>
                              <StyledSelector
                                value={formData.quality}
                                onChange={(v) => setFormData({...formData, quality: v})}
                                placeholder="Choose quality"
                                options={[
                                  { id: 'Concept', name: 'Concept', subtitle: 'Quick Validation' },
                                  { id: 'Prototype', name: 'Prototype', subtitle: 'Functional Testing' },
                                  { id: 'Test/Demo', name: 'Test/Demo', subtitle: 'Demonstration Ready' },
                                  { id: 'End Use', name: 'End Use', subtitle: 'Production Quality' },
                                  { id: 'Display', name: 'Display', subtitle: 'Premium Finish' },
                                ]}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Step 1: Options */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 1 ? 'opacity-100 translate-x-0' : quoteStep > 1 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Print Options</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Use Case</Label>
                              <StyledSelector
                                value={formData.strength}
                                onChange={(v) => setFormData({...formData, strength: v})}
                                placeholder="Choose use case"
                                options={[
                                  { id: 'Cosmetic Only', name: 'Cosmetic Only', subtitle: 'Display Model' },
                                  { id: 'Reinforced', name: 'Reinforced', subtitle: 'Light Use' },
                                  { id: 'Functional', name: 'Functional', subtitle: 'Heavy Duty' },
                                ]}
                              />
                            </div>
                            <div>
                              <Label className="text-base font-medium">Surface Smoothing</Label>
                              <StyledSelector
                                value={formData.smoothing}
                                onChange={(v) => setFormData({...formData, smoothing: v})}
                                placeholder="Choose smoothing"
                                options={[
                                  { id: 'None - As Printed', name: 'None', subtitle: 'As Printed' },
                                  { id: 'Light Sanding', name: 'Light Sanding' },
                                  { id: 'Smooth Finish', name: 'Smooth Finish' },
                                  { id: 'Premium Finish', name: 'Premium Finish' },
                                ]}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Step 2: Color & Quantity */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 2 ? 'opacity-100 translate-x-0' : quoteStep > 2 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Color & Quantity</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Color</Label>
                              <StyledSelector
                                value={formData.color}
                                onChange={(v) => setFormData({...formData, color: v})}
                                placeholder="Choose color"
                                options={[
                                  { id: 'Any', name: 'Any', subtitle: 'We choose' },
                                  { id: 'Black', name: 'Black' },
                                  { id: 'White', name: 'White' },
                                  { id: 'Grey', name: 'Grey' },
                                  { id: 'Red', name: 'Red' },
                                  { id: 'Blue', name: 'Blue' },
                                  { id: 'Other', name: 'Other', subtitle: 'specify below' },
                                ]}
                              />
                              {formData.color === 'Other' && (
                                <Input
                                  type="text"
                                  placeholder="e.g., Green, Yellow"
                                  value={formData.customColor}
                                  onChange={(e) => setFormData({...formData, customColor: e.target.value})}
                                  className="mt-3"
                                />
                              )}
                            </div>
                            <div>
                              <Label className="text-base font-medium">Quantity</Label>
                              <input
                                type="number"
                                min="1"
                                max="1000"
                                value={formData.quantity}
                                onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                                className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none text-lg font-semibold"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Step 3: Contact Info */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 3 ? 'opacity-100 translate-x-0' : quoteStep > 3 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Name</Label>
                              <input
                                type="text"
                                placeholder="Your name"
                                value={formData.contact_name}
                                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                                className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                              />
                            </div>
                            <div>
                              <Label className="text-base font-medium">Email</Label>
                              <input
                                type="email"
                                placeholder="you@example.com"
                                value={formData.contact_email}
                                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                                required
                                className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Step 4: Shipping */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 4 ? 'opacity-100 translate-x-0' : quoteStep > 4 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Shipping Details</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Shipping Destination</Label>
                              <StyledSelector
                                value={formData.country_code}
                                onChange={(v) => setFormData({...formData, country_code: v})}
                                placeholder="Choose destination"
                                options={[
                                  { id: 'CA', name: 'Canada' },
                                  { id: 'US', name: 'United States' },
                                ]}
                              />
                            </div>
                            <div>
                              <Label className="text-base font-medium">Postal/Zip Code (Optional)</Label>
                              <input
                                type="text"
                                placeholder={formData.country_code === "CA" ? "A1A 1A1" : "12345"}
                                value={formData.postal_code}
                                onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                                className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Step 5: Notes & Submit */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 5 ? 'opacity-100 translate-x-0' : quoteStep > 5 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Additional Notes</h3>
                          <div>
                            <Label className="text-base font-medium">Special Instructions (Optional)</Label>
                            <textarea
                              placeholder="Any special requirements or questions..."
                              value={formData.notes}
                              onChange={(e) => setFormData({...formData, notes: e.target.value})}
                              rows={4}
                              className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none resize-none"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Navigation */}
                      <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                        <button
                          type="button"
                          onClick={prevQuoteStep}
                          className={`flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors ${quoteStep === 0 ? 'invisible' : ''}`}
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back
                        </button>
                        
                        {quoteStep < totalQuoteSteps - 1 ? (
                          <button
                            type="button"
                            onClick={nextQuoteStep}
                            className="flex items-center gap-2 px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#000690] transition-colors font-medium"
                          >
                            Next
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        ) : (
                          <Button 
                            type="submit" 
                            disabled={loadingQuote || !formData.file}
                            className="px-8 py-3 bg-[#0007b5] hover:bg-[#000690] text-white text-lg font-semibold rounded-xl"
                          >
                            {loadingQuote ? "Generating Quote..." : "Get Instant Quote"}
                          </Button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </TabsContent>

              {/* Quote Results Tab */}
              <TabsContent value="quote" className="mt-0">
                <div className="p-8">
                  {quoteData ? (
                    <Card>
                      <CardHeader>
                        <h2 className="text-2xl font-bold text-slate-900">Your Quote</h2>
                      </CardHeader>
                      <CardContent>
                        <div className="text-center py-8">
                          <div className="text-4xl font-bold text-[#0007b5] mb-4">
                            ${quoteData.total_price?.toFixed(2) || '0.00'}
                          </div>
                          <p className="text-slate-500">Quote sent to {formData.contact_email}</p>
                          <Button 
                            className="mt-6 bg-[#0007b5] hover:bg-[#000690]"
                            onClick={() => {
                              setActiveTab('on-demand-3d-print');
                              setQuoteStep(0);
                            }}
                          >
                            Get Another Quote
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-slate-500">Submit a quote request to see results here.</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Design Tab - Full Wizard Form */}
              <TabsContent value="design" className="mt-0">
                <DesignServiceWizard onSubmit={handleServiceRequest} onBackToServices={handleBackToServices} />
              </TabsContent>

              {/* 2D to 3D Tab - Full Wizard Form */}
              <TabsContent value="convert" className="mt-0">
                <ServiceRequestWizard 
                  title="2D to 3D Conversion"
                  description="Convert your 2D drawings, blueprints, or sketches into printable 3D models."
                  serviceType="2D to 3D Conversion"
                  fileRequired={true}
                  onSubmit={handleServiceRequest}
                  onBackToServices={handleBackToServices}
                />
              </TabsContent>

              {/* Optimize Tab - Full Wizard Form with 3D Preview */}
              <TabsContent value="optimize" className="mt-0">
                <ServiceRequestWizardWith3DPreview 
                  title="Model Optimization"
                  description="Optimize your 3D models for better printability, strength, and efficiency."
                  serviceType="Product Optimization"
                  onSubmit={handleServiceRequest}
                  onBackToServices={handleBackToServices}
                />
              </TabsContent>

              {/* Renders Tab - Full Wizard Form with 3D Preview */}
              <TabsContent value="renders" className="mt-0">
                <ServiceRequestWizardWith3DPreview 
                  title="Product Renders"
                  description="Get photorealistic renders of your 3D models for marketing and presentations."
                  serviceType="Product Renders"
                  onSubmit={handleServiceRequest}
                  onBackToServices={handleBackToServices}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Bottom spacing */}
        <div className="py-16" />
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ServicesToolPage;
